import { useState } from 'react'
import { slugifyName, formatDisplayName } from '../../lib/visitorContext'

const BASE_URL = 'https://aqibnawab.vercel.app'

const OUTREACH_PLATFORMS = [
  { id: 'linkedin', label: 'LinkedIn', emoji: '💼' },
  { id: 'twitter', label: 'X / Twitter', emoji: '𝕏' },
  { id: 'whatsapp', label: 'WhatsApp', emoji: '💬' },
  { id: 'email', label: 'Email', emoji: '📧' },
]

const PUBLIC_PLATFORMS = [
  { id: 'github', label: 'GitHub', emoji: '🐙' },
  { id: 'linkedin', label: 'LinkedIn', emoji: '💼' },
  { id: 'twitter', label: 'X / Twitter', emoji: '𝕏' },
  { id: 'medium', label: 'Medium', emoji: '📝' },
  { id: 'devto', label: 'Dev.to', emoji: '👩‍💻' },
  { id: 'resume', label: 'Resume / PDF', emoji: '📄' },
  { id: 'email', label: 'Email signature', emoji: '📧' },
  { id: 'other', label: 'Other', emoji: '🔗' },
]

const PUBLIC_DESTINATIONS = [
  { id: 'home', label: 'Portfolio (Home)', path: '/' },
  { id: 'projects', label: 'Projects', path: '/projects' },
  { id: 'blogs', label: 'Blogs', path: '/blogs' },
]

const buildPublicLink = (path, platform) => {
  const params = new URLSearchParams({ utm_source: platform })
  const base = `${BASE_URL}${path === '/' ? '' : path}`
  return `${base}?${params.toString()}`
}

const buildOutreachLinks = (name, platform) => {
  const slug = slugifyName(name)
  if (!slug) return null

  const hi = `${BASE_URL}/hi/${slug}`
  const params = new URLSearchParams({
    utm_source: platform,
    utm_medium: 'outreach',
    utm_campaign: 'portfolio',
  })

  return {
    slug,
    displayName: formatDisplayName(slug),
    hiLink: hi,
    trackedLink: `${hi}?${params.toString()}`,
    refLink: `${BASE_URL}/?ref=${slug}&${params.toString()}`,
  }
}

export default function ShareLinkBuilder() {
  const [publicPlatform, setPublicPlatform] = useState('github')
  const [name, setName] = useState('')
  const [outreachPlatform, setOutreachPlatform] = useState('linkedin')
  const [copied, setCopied] = useState('')

  const outreachLinks = name.trim() ? buildOutreachLinks(name, outreachPlatform) : null

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(key)
      setTimeout(() => setCopied(''), 2000)
    } catch {
      setCopied('')
    }
  }

  const CopyRow = ({ label, url, copyKey }) => (
    <div className="dash__share-row">
      <span className="dash__share-label">{label}</span>
      <code className="dash__share-url">{url}</code>
      <button
        type="button"
        className="dash__copy-btn"
        onClick={() => copy(url, copyKey)}
      >
        {copied === copyKey ? 'Copied!' : 'Copy'}
      </button>
    </div>
  )

  return (
    <div className="dash__share-wrap">
      <section className="dash__share">
        <h3 className="dash__section-title">Public Links</h3>
        <p className="dash__share-desc">
          Static tracked links for GitHub README, LinkedIn bio, blog posts, and
          anywhere you share your portfolio — no founder name needed.
        </p>

        <div className="dash__share-form dash__share-form--single">
          <label className="dash__share-field-label" htmlFor="public-platform">
            Where will you share this?
          </label>
          <select
            id="public-platform"
            className="dash__share-select"
            value={publicPlatform}
            onChange={(e) => setPublicPlatform(e.target.value)}
          >
            {PUBLIC_PLATFORMS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.emoji} {p.label}
              </option>
            ))}
          </select>
        </div>

        <div className="dash__share-results">
          {PUBLIC_DESTINATIONS.map((dest) => {
            const url = buildPublicLink(dest.path, publicPlatform)
            return (
              <CopyRow
                key={dest.id}
                label={dest.label}
                url={url}
                copyKey={`public-${dest.id}`}
              />
            )
          })}
        </div>

        <p className="dash__share-note">
          Tracks visits via <code>utm_source</code> only — e.g.{' '}
          <strong>{publicPlatform}</strong> shows up in your dashboard source column.
        </p>
      </section>

      <section className="dash__share dash__share--outreach">
        <h3 className="dash__section-title">Founder Outreach Links</h3>
        <p className="dash__share-desc">
          Personalized links that greet the founder by name in the hero when they open your portfolio.
        </p>

        <div className="dash__share-form">
          <input
            type="text"
            className="dash__share-input"
            placeholder="Founder name e.g. Sarah Chen"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <select
            className="dash__share-select"
            value={outreachPlatform}
            onChange={(e) => setOutreachPlatform(e.target.value)}
          >
            {OUTREACH_PLATFORMS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.emoji} {p.label}
              </option>
            ))}
          </select>
        </div>

        {outreachLinks ? (
          <div className="dash__share-results">
            <CopyRow
              label="Hi link (recommended)"
              url={outreachLinks.trackedLink}
              copyKey="hi"
            />
            <CopyRow
              label="Plain hi link"
              url={outreachLinks.hiLink}
              copyKey="plain"
            />
            <CopyRow
              label="Ref on homepage"
              url={outreachLinks.refLink}
              copyKey="ref"
            />
            <p className="dash__share-preview">
              Preview greeting: <strong>Hi, {outreachLinks.displayName} 👋</strong>
            </p>
          </div>
        ) : (
          <p className="dash__share-empty">
            Enter a founder name above to generate personalized outreach links.
          </p>
        )}
      </section>
    </div>
  )
}
